import { Grid } from '@material-ui/core';
import { isEqual } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { UomText } from 'src/common/enumerations/unit-of-measure-type';
import { getPriceFromItems } from 'src/common/helpers/action-window-helper';
import { IItemLookup } from 'src/common/interfaces/dtos/IItemLookup';
import { IElectronicJournalRecord } from 'src/common/interfaces/states/IElectronicJournalRecord';
import { getProductCount } from 'src/total/business-logic/product-count';
import { ReactComponent as TareLink } from '../../../common/media/link.svg';
import { styles } from './bottle-return-list-styles';
import BottleDepositHeader from '../bottle-deposit-header/bottle-deposit-header';
import { convertCurrency } from '../../../common/helpers/currency-helper';

interface IBottleReturnListProps {
  listings: IItemLookup[];
  selectedBottleReturnItemCode?: string;
  itemsReturnedInCurrentTransaction: IElectronicJournalRecord[];
  onClick: (itemId: string) => void;
}

const BottleReturnList: React.FC<IBottleReturnListProps> = ({
  listings,
  selectedBottleReturnItemCode,
  onClick,
  itemsReturnedInCurrentTransaction,
}: IBottleReturnListProps): JSX.Element | null => {
  const classes = styles();
  return (
    <Grid
      container
      direction='column'
      data-testid='bottle-deposit-results'
      className={classes.bottleDepositItems}
    >
      <BottleDepositHeader />
      <Grid item>
        {listings.map(
          (listing): JSX.Element => {
            const selected: boolean =
              !!selectedBottleReturnItemCode && listing.itemId === selectedBottleReturnItemCode;
            const lineItemsForCurrentItemCode = itemsReturnedInCurrentTransaction.filter(
              i => i.ItemId === listing.itemId,
            );
            return (
              <BottleReturnItemListing
                key={listing.itemId}
                listing={listing}
                selected={selected}
                lineItemsForCurrentItemCode={lineItemsForCurrentItemCode}
                onClick={onClick}
              />
            );
          },
        )}
      </Grid>
    </Grid>
  );
};
interface IBottleReturnItemListingProps {
  listing: IItemLookup;
  selected: boolean;
  lineItemsForCurrentItemCode: IElectronicJournalRecord[];
  onClick: (itemId: string) => void;
}

export const BottleReturnItemListing = (props: IBottleReturnItemListingProps): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();
  const { listing, selected, onClick, lineItemsForCurrentItemCode } = props;
  const { description, unitPrice } = listing;
  const className = `result ${selected ? `${classes.selected}` : ''}`;
  const hasBottleReturnQuantity = lineItemsForCurrentItemCode.length > 0;
  const currentLineItems = lineItemsForCurrentItemCode.filter(
    item => item.ItemId === listing.itemId,
  );
  const itemCount = getProductCount(lineItemsForCurrentItemCode, true);
  return (
    <Grid
      direction='column'
      spacing={0}
      wrap='nowrap'
      container
      onClick={() => onClick(listing.itemId ?? '')}
      className={`${classes.bottleDepositItemContainer} ${className}`}
      data-testid={`bottle-deposit-result-${listing.itemId}${selected ? '-selected' : ''}`}
    >
      {/* Main part of bottle return items list */}
      <Grid item className='fullHeight'>
        <Grid container justifyContent='flex-start' className={classes.row}>
          <Grid item xs={4} className={classes.bottleDepositPriceContainer}>
            <Grid container item xs={9} className={classes.extendWidth}>
              {hasBottleReturnQuantity && (
                <Grid item className={classes.icon}>
                  <TareLink />
                </Grid>
              )}
              <Grid item className={classes.bottleDepositProductInformation}>
                {description}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={3} className={classes.bottleDepositPriceContainer}>
            <Grid container item xs={9} justifyContent={'center'}>
              <Grid item className={classes.bottleDepositProductInformation}>
                {hasBottleReturnQuantity &&
                  currentLineItems &&
                  `${convertCurrency(Number(unitPrice))} / Unit`}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={2} className={classes.bottleDepositPriceContainer}>
            <Grid container item xs={9} justifyContent={'center'}>
              <Grid item className={classes.bottleDepositProductInformation}>
                {hasBottleReturnQuantity &&
                  `${itemCount} ${t(
                    itemCount === 1 ? UomText.EA : 'BottleDeposit_QuantityCount_Plural',
                  )}`}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={3} className={classes.bottleDepositPriceContainer}>
            <Grid container item xs={9} justifyContent={'center'} className={classes.floatRight}>
              <Grid item className={classes.bottleDepositProductInformation}>
                {currentLineItems && getPriceFromItems(currentLineItems)}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const shouldNotReRender = (prevProps, nextProps) => {
  const isRender =
    !isEqual(prevProps.listings, nextProps.listings) ||
    prevProps.selectedBottleReturnItemCode !== nextProps.selectedBottleReturnItemCode ||
    !isEqual(
      prevProps.itemsReturnedInCurrentTransaction,
      nextProps.itemsReturnedInCurrentTransaction,
    );
  return !isRender;
};
export default React.memo(BottleReturnList, shouldNotReRender);
